/**
 * RTK Query endpoints for the secrets service (@cluster/secrets).
 * Base path after the dev proxy + nginx rewrite strips `/api`: `/secrets`.
 * SEC: the vault list/detail endpoints expose KEYS ONLY; values are read with
 * an explicit POST /secrets/vaults/:id/read per key.
 */
import { apiSlice } from './apiSlice';
import type {
  Vault,
  VaultReadResponse,
} from '@/types';

function aliasId<T extends object>(entry: T): T & { _id: string } {
  const e = entry as T & { id?: string; _id?: string };
  return { ...entry, _id: e.id ?? e._id ?? '' };
}

function dataList<T extends object>(response: { data: T[] }): T[] {
  return response.data.map((x) => aliasId<T>(x));
}

function dataOne<T extends object>(response: { data: T }): T & { _id: string } {
  return aliasId<T>(response.data);
}

interface VaultPayload extends Omit<Vault, 'entries'> {
  entries?: Vault['entries'];
}

export interface CreateVaultDto {
  name: string;
  description?: string;
  projectId?: string;
  settings?: { allowAgentRead?: boolean };
  /** Plaintext at creation — the service encrypts each value (AES-256-GCM) at rest. */
  entries?: SetVaultEntryDto[];
}

export type UpdateVaultDto = Partial<Omit<CreateVaultDto, 'entries'>> & {
  /** Patch semantics: {key,value} → upsert; {key} only → remove. */
  entries?: Array<{ key: string; value?: string }>;
};

export interface SetVaultEntryDto {
  key: string;
  value: string;
}

export const secretsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getVaults: builder.query<Vault[], void>({
      query: () => '/secrets/vaults',
      transformResponse: (response: { data: Vault[] }) => dataList<Vault>(response),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Vault' as const, id: _id })),
              { type: 'Vault', id: 'LIST' },
            ]
          : [{ type: 'Vault', id: 'LIST' }],
    }),

    getVault: builder.query<Vault, string>({
      query: (id) => `/secrets/vaults/${id}`,
      transformResponse: (response: { data: Vault }) => dataOne<Vault>(response),
      providesTags: (_result, _error, id) => [{ type: 'Vault', id }],
    }),

    createVault: builder.mutation<Vault, CreateVaultDto>({
      query: (body) => ({ url: '/secrets/vaults', method: 'POST', body }),
      transformResponse: (response: { data: Vault }) => dataOne<Vault>(response),
      invalidatesTags: [{ type: 'Vault', id: 'LIST' }],
    }),

    updateVault: builder.mutation<Vault, { id: string; body: UpdateVaultDto }>({
      query: ({ id, body }) => ({ url: `/secrets/vaults/${id}`, method: 'PATCH', body }),
      transformResponse: (response: { data: Vault }) => dataOne<Vault>(response),
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'Vault', id }, { type: 'Vault', id: 'LIST' }]
          : [{ type: 'Vault', id: 'LIST' }],
    }),

    deleteVault: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/secrets/vaults/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Vault', id: 'LIST' }],
    }),

    /** POST /secrets/vaults/:id/read {key} → { value } — the ONLY value read path. */
    readVaultSecret: builder.mutation<VaultReadResponse, { id: string; key: string }>({
      query: ({ id, key }) => ({
        url: `/secrets/vaults/${id}/read`,
        method: 'POST',
        body: { key },
      }),
    }),

    /**
     * Upsert a secret entry. Backend contract is PATCH /secrets/vaults/:id with
     * body `{ entries: [{ key, value }] }` — the loose `{ key, value }` shape is
     * NOT a field of UpdateVaultDto, so the service ValidationPipe
     * (whitelist+forbidNonWhitelisted) would 400-strip it and nothing persists.
     * The public call signature stays `{ id, body: {key,value} }`; the wrapper is
     * built here so callers (SecretsPage) don't change.
     */
    saveVaultEntry: builder.mutation<
      Vault,
      { id: string; body: SetVaultEntryDto }
    >({
      query: ({ id, body }) => ({
        url: `/secrets/vaults/${id}`,
        method: 'PATCH',
        body: { entries: [body] },
      }),
      transformResponse: (response: { data: Vault }) => dataOne<Vault>(response),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Vault', id },
        { type: 'Vault', id: 'LIST' },
      ],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetVaultsQuery,
  useGetVaultQuery,
  useCreateVaultMutation,
  useUpdateVaultMutation,
  useDeleteVaultMutation,
  useReadVaultSecretMutation,
  useSaveVaultEntryMutation,
} = secretsApi;

export type { VaultPayload };